import { IStatus } from "@/Common/api/types/CommissionCycle";
import { getPrioritizedStatus, StatusKey } from "./Statuses";
import { isEmpty } from "lodash";

const getPeriodsPartsMap = (positionStatuses: IStatus[]) => {
	const allPositions = positionStatuses.map((item) => item.position);
	const uniqueAllPositions = [...new Set(allPositions)];

	return uniqueAllPositions.reduce(
		(acc, position) => {
			const parts = positionStatuses.filter((item) => item.position === position);
			acc[position as keyof object] = [...parts];
			return acc;
		},
		{} as Record<string, IStatus[]>
	);
};

const getCombinedStatuses = (positionStatuses: IStatus[]) => {
	const allPositions = getPeriodsPartsMap(positionStatuses);
	if (isEmpty(allPositions)) {
		return [];
	}
	return Object.entries(allPositions).map(([position, statuses]) => {
		const [firstStatus, secondStatus] = statuses ?? [];
		const status = getPrioritizedStatus(firstStatus, secondStatus);
		return {
			position,
			status,
		};
	});
};

const resolveDisabledPositionsByStatus = ({
	positionStatuses = [],
	allowedStatuses,
}: {
	positionStatuses?: IStatus[];
	allowedStatuses: string[];
}) => {
	const combinedStatuses = getCombinedStatuses(positionStatuses);
	return combinedStatuses.filter((item) => !allowedStatuses.includes(item.status)).map((item) => item.position);
};

const resolveDisabledPositionsByWashoutConditions = ({
	positionStatuses = [],
	washoutConditionsByPosition,
	statusKey,
}: {
	positionStatuses?: IStatus[];
	washoutConditionsByPosition: Record<string, StatusKey[]>;
	statusKey: StatusKey;
}) => {
	return positionStatuses
		.filter((item) => !washoutConditionsByPosition?.[item.position]?.includes(statusKey))
		.map((item) => item.position);
};

const isRelativeToSingleCycle = (cycles: string[] = []) => Boolean(cycles?.length === 1);

const disabledPositionsRelativeToCycles = (args: { positionStatuses?: IStatus[]; allowedStatuses: string[]; cycles: string[] }) => {
	const { positionStatuses = [], allowedStatuses = [], cycles = [] } = args ?? {};
	if (!isRelativeToSingleCycle(cycles)) {
		return [];
	}
	let positions: string[] = [];
	for (const ccId of cycles) {
		const filteredStatuses = positionStatuses.filter((item) => item.ccId === ccId);
		const results = resolveDisabledPositionsByStatus({ positionStatuses: filteredStatuses, allowedStatuses });
		positions = [...positions, ...results];
	}

	return [...new Set(positions)];
};

export { resolveDisabledPositionsByStatus, disabledPositionsRelativeToCycles, resolveDisabledPositionsByWashoutConditions };
